import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, TextField, Tabs, Tab, Grid,
  Card, CardContent, Alert, AlertTitle, Dialog, DialogTitle,
  DialogContent, DialogActions, Select, MenuItem, FormControl,
  InputLabel, Divider, Snackbar, IconButton, CircularProgress,
  TablePagination
} from '@mui/material';
import {
  SupervisorAccount, Storage, History, Settings, Warning,
  Download, Refresh, Close, Search, Edit, Save, Cancel
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import apiClient from '../../services/apiClient';

const actionColors = {
  CREATE_USER: 'success', DELETE_USER: 'error', UPDATE_USER: 'info',
  UNLOCK_USER: 'warning', CHANGE_PASSWORD: 'secondary',
  APPROVE_APPLICATION: 'success', REJECT_APPLICATION: 'error',
  REVIEW_APPLICATION: 'info', REOPEN_APPLICATION: 'warning',
  UPDATE_APPLICATION: 'info',
  UPDATE_USER_STATUS: 'warning', UPDATE_STUDENT_GRADE: 'info',
  ARCHIVE_APPLICATIONS: 'secondary', PURGE_REJECTED_APPLICATIONS: 'error',
  CONFIG_UPDATE: 'secondary',
};

const BULK_OPERATIONS = [
  { value: 'update_user_status', label: 'Update User Status', params: ['fromStatus', 'value'], paramLabels: ['Current Status (e.g. inactive)', 'New Status (e.g. active)'] },
  { value: 'update_student_grade', label: 'Update Student Grade Level', params: ['fromGrade', 'value'], paramLabels: ['Current Grade (e.g. 5)', 'New Grade (e.g. 6)'] },
  { value: 'archive_applications', label: 'Move Stale Pending Applications to Reviewing', params: [], paramLabels: [], description: 'Applications pending > 90 days' },
  { value: 'purge_rejected_applications', label: 'Purge Old Rejected Applications', params: [], paramLabels: [], description: 'Rejected applications reviewed > 12 months ago — IRREVERSIBLE' },
];

const SuperUserAdmin = () => {
  const location = useLocation();
  const getInitialTab = () => {
    if (location.pathname.includes('/bulk')) return 1;
    if (location.pathname.includes('/audit')) return 2;
    if (location.pathname.includes('/config')) return 3;
    return 0;
  };

  const [tab, setTab] = useState(getInitialTab());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // — Data Management state —
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);

  // — Audit Log state —
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(0);
  const [auditRowsPerPage, setAuditRowsPerPage] = useState(25);
  const [auditFilters, setAuditFilters] = useState({ action: '', actorEmail: '', startDate: '', endDate: '' });

  // — Bulk Operations state —
  const [bulkOp, setBulkOp] = useState('');
  const [bulkParams, setBulkParams] = useState({});
  const [previewResult, setPreviewResult] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false });
  const [confirmInput, setConfirmInput] = useState('');

  // — System Config state —
  const [config, setConfig] = useState([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSearch, setConfigSearch] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  // ── Data Management ──────────────────────────────────────────────────────
  const loadTables = useCallback(async () => {
    setTablesLoading(true);
    try {
      const data = await apiClient.getDbStats();
      setTables(data.tables || []);
    } catch (err) {
      notify(`Failed to load table stats: ${err.message}`, 'error');
    } finally {
      setTablesLoading(false);
    }
  }, []);

  // ── Audit Log ────────────────────────────────────────────────────────────
  const loadAuditLog = useCallback(async () => {
    setAuditLoading(true);
    try {
      const data = await apiClient.getAuditLog({
        ...auditFilters,
        limit: auditRowsPerPage,
        offset: auditPage * auditRowsPerPage
      });
      setAuditLogs(data.logs || []);
      setAuditTotal(data.total || 0);
    } catch (err) {
      notify(`Failed to load audit log: ${err.message}`, 'error');
    } finally {
      setAuditLoading(false);
    }
  }, [auditFilters, auditPage, auditRowsPerPage]);

  // ── System Config ────────────────────────────────────────────────────────
  const loadConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const data = await apiClient.getSystemConfig();
      setConfig(data.config || []);
    } catch (err) {
      notify(`Failed to load system config: ${err.message}`, 'error');
    } finally {
      setConfigLoading(false);
    }
  }, []);

  // Load data when tab becomes active
  useEffect(() => {
    if (tab === 0) loadTables();
    if (tab === 2) loadAuditLog();
    if (tab === 3) loadConfig();
  }, [tab, loadTables, loadAuditLog, loadConfig]);

  // Reload audit log when page/filter changes
  useEffect(() => {
    if (tab === 2) loadAuditLog();
  }, [auditPage, auditRowsPerPage, tab, loadAuditLog]);

  // ── Bulk Operations ──────────────────────────────────────────────────────
  const selectedOp = BULK_OPERATIONS.find(o => o.value === bulkOp);

  const handleBulkPreview = async () => {
    setBulkLoading(true);
    setPreviewResult(null);
    try {
      const result = await apiClient.bulkOperation(bulkOp, bulkParams, true);
      setPreviewResult(result);
    } catch (err) {
      notify(`Preview failed: ${err.message}`, 'error');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkExecute = async () => {
    if (confirmInput !== 'CONFIRM') {
      notify('Type CONFIRM to proceed', 'error');
      return;
    }
    setBulkLoading(true);
    setConfirmDialog({ open: false });
    setConfirmInput('');
    try {
      const result = await apiClient.bulkOperation(bulkOp, bulkParams, false);
      notify(`Operation complete — ${result.affected_count} records affected`, 'success');
      setPreviewResult(null);
      setBulkOp('');
      setBulkParams({});
    } catch (err) {
      notify(`Operation failed: ${err.message}`, 'error');
    } finally {
      setBulkLoading(false);
    }
  };

  // ── System Config editing ────────────────────────────────────────────────
  const handleConfigSave = async (key) => {
    try {
      await apiClient.updateSystemConfig(key, editingValue);
      setConfig(prev => prev.map(c => c.key === key ? { ...c, value: editingValue } : c));
      setEditingKey(null);
      notify(`${key} updated`);
    } catch (err) {
      notify(`Failed to update: ${err.message}`, 'error');
    }
  };

  const filteredConfig = config.filter(c =>
    !configSearch || c.key.toLowerCase().includes(configSearch.toLowerCase()) || c.value.toLowerCase().includes(configSearch.toLowerCase())
  );

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <SupervisorAccount sx={{ color: '#4a148c' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Super User Console</Typography>
          <Chip label="Super User Only" sx={{ bgcolor: '#4a148c', color: 'white', fontSize: '0.7rem' }} size="small" />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Direct database access, bulk operations, and system configuration. All actions are logged in the audit trail.
        </Typography>
      </Box>

      <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
        <AlertTitle>Super User Access</AlertTitle>
        You are operating with elevated privileges. All actions performed here are logged and many cannot be undone.
      </Alert>

      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab icon={<Storage />} label="Data Management" iconPosition="start" />
            <Tab icon={<Warning />} label="Bulk Operations" iconPosition="start" />
            <Tab icon={<History />} label="Audit Log" iconPosition="start" />
            <Tab icon={<Settings />} label="System Config" iconPosition="start" />
          </Tabs>
        </Box>

        {/* ── Data Management ── */}
        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Database Tables — era schema</Typography>
              <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadTables} disabled={tablesLoading}>
                Refresh
              </Button>
            </Box>
            {tablesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Table</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Live Rows</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Total Size</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Last Analyze</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tables.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No tables found in era schema
                        </TableCell>
                      </TableRow>
                    ) : tables.map(t => (
                      <TableRow key={t.table_name} hover>
                        <TableCell>
                          <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>era.{t.table_name}</Typography>
                        </TableCell>
                        <TableCell align="right">{Number(t.row_count).toLocaleString()}</TableCell>
                        <TableCell align="right">{t.total_size}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {t.last_analyze ? new Date(t.last_analyze).toLocaleString() : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Row counts are from <code>pg_stat_user_tables</code> (n_live_tup). Run <code>ANALYZE</code> in the database for the most accurate counts.
              </Alert>
            </Box>
          </Box>
        )}

        {/* ── Bulk Operations ── */}
        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Bulk Operation</Typography>
                <Card elevation={1} sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Alert severity="warning" sx={{ borderRadius: 1 }}>
                      Bulk operations modify or delete multiple records at once. Always preview before executing.
                    </Alert>
                    <FormControl fullWidth size="small">
                      <InputLabel>Operation</InputLabel>
                      <Select value={bulkOp} label="Operation" onChange={e => { setBulkOp(e.target.value); setBulkParams({}); setPreviewResult(null); }}>
                        {BULK_OPERATIONS.map(o => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {selectedOp?.description && (
                      <Alert severity="info" sx={{ borderRadius: 1 }}>{selectedOp.description}</Alert>
                    )}

                    {selectedOp?.params.map((param, i) => (
                      <TextField
                        key={param}
                        label={selectedOp.paramLabels[i]}
                        fullWidth size="small"
                        value={bulkParams[param] || ''}
                        onChange={e => setBulkParams(p => ({ ...p, [param]: e.target.value }))}
                      />
                    ))}

                    {previewResult && (
                      <Alert severity={previewResult.affected_count > 0 ? 'warning' : 'success'} sx={{ borderRadius: 1 }}>
                        Preview: <strong>{previewResult.affected_count}</strong> record(s) would be affected.
                        {previewResult.affected_count === 0 && ' No records match the criteria.'}
                      </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined" startIcon={<Search />}
                        disabled={!bulkOp || bulkLoading}
                        onClick={handleBulkPreview}
                      >
                        Preview
                      </Button>
                      <Button
                        variant="contained" color="warning" startIcon={<Warning />}
                        disabled={!bulkOp || bulkLoading || !previewResult || previewResult.affected_count === 0}
                        onClick={() => setConfirmDialog({ open: true })}
                      >
                        {bulkLoading ? <CircularProgress size={18} color="inherit" /> : 'Execute'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={5}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Available Operations</Typography>
                {BULK_OPERATIONS.map(op => (
                  <Box key={op.value} sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{op.label}</Typography>
                    {op.description && <Typography variant="caption" color="text.secondary">{op.description}</Typography>}
                  </Box>
                ))}
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ── Audit Log ── */}
        {tab === 2 && (
          <Box>
            <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>Audit Log</Typography>
              <TextField
                label="Action" size="small" sx={{ width: 160 }}
                value={auditFilters.action}
                onChange={e => setAuditFilters(p => ({ ...p, action: e.target.value }))}
                placeholder="e.g. DELETE_USER"
              />
              <TextField
                label="Actor Email" size="small" sx={{ width: 200 }}
                value={auditFilters.actorEmail}
                onChange={e => setAuditFilters(p => ({ ...p, actorEmail: e.target.value }))}
              />
              <TextField
                label="Start Date" type="date" size="small" InputLabelProps={{ shrink: true }}
                value={auditFilters.startDate}
                onChange={e => setAuditFilters(p => ({ ...p, startDate: e.target.value }))}
              />
              <TextField
                label="End Date" type="date" size="small" InputLabelProps={{ shrink: true }}
                value={auditFilters.endDate}
                onChange={e => setAuditFilters(p => ({ ...p, endDate: e.target.value }))}
              />
              <Button variant="outlined" size="small" onClick={() => { setAuditPage(0); loadAuditLog(); }} disabled={auditLoading}>
                Search
              </Button>
              <Button size="small" onClick={() => { setAuditFilters({ action: '', actorEmail: '', startDate: '', endDate: '' }); setAuditPage(0); }}>
                Clear
              </Button>
            </Box>

            {auditLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actor</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Target</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {auditLogs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            No audit log entries found
                          </TableCell>
                        </TableRow>
                      ) : auditLogs.map(log => (
                        <TableRow key={log.log_id} hover>
                          <TableCell>
                            <Chip label={log.action} color={actionColors[log.action] || 'default'} size="small" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{log.actor_email || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', px: 1, py: 0.5, borderRadius: 1 }}>
                              {log.target_type}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" noWrap sx={{ maxWidth: 220, display: 'block' }}>
                              {log.target_description || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{new Date(log.created_at).toLocaleString()}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={auditTotal}
                  page={auditPage}
                  rowsPerPage={auditRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  onPageChange={(e, p) => setAuditPage(p)}
                  onRowsPerPageChange={e => { setAuditRowsPerPage(parseInt(e.target.value)); setAuditPage(0); }}
                />
              </>
            )}
          </Box>
        )}

        {/* ── System Config ── */}
        {tab === 3 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, gap: 2, alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>System Configuration</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  placeholder="Search keys or values..."
                  value={configSearch}
                  onChange={e => setConfigSearch(e.target.value)}
                  size="small"
                  sx={{ minWidth: 240 }}
                  InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.disabled', fontSize: 18 }} /> }}
                />
                <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadConfig} disabled={configLoading}>
                  Refresh
                </Button>
              </Box>
            </Box>
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              Read-only values are managed at the infrastructure level. Changes to editable values take effect immediately.
            </Alert>
            {configLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Key</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 120 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredConfig.map(c => (
                      <TableRow key={c.key} hover>
                        <TableCell>
                          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{c.key}</Typography>
                        </TableCell>
                        <TableCell>
                          {editingKey === c.key ? (
                            <TextField
                              value={editingValue}
                              onChange={e => setEditingValue(e.target.value)}
                              size="small"
                              fullWidth
                              autoFocus
                              onKeyDown={e => { if (e.key === 'Enter') handleConfigSave(c.key); if (e.key === 'Escape') setEditingKey(null); }}
                            />
                          ) : (
                            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'text.secondary' }}>{c.value}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">{c.description || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          {editingKey === c.key ? (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton size="small" color="success" onClick={() => handleConfigSave(c.key)}><Save fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={() => setEditingKey(null)}><Cancel fontSize="small" /></IconButton>
                            </Box>
                          ) : c.editable ? (
                            <IconButton size="small" onClick={() => { setEditingKey(c.key); setEditingValue(c.value); }}>
                              <Edit fontSize="small" />
                            </IconButton>
                          ) : (
                            <Chip label="Read-only" size="small" variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredConfig.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No config keys match your search
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* Bulk Operation Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => { setConfirmDialog({ open: false }); setConfirmInput(''); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning /> Confirm Bulk Operation
          <IconButton size="small" sx={{ ml: 'auto' }} onClick={() => { setConfirmDialog({ open: false }); setConfirmInput(''); }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will affect <strong>{previewResult?.affected_count}</strong> record(s).
            {bulkOp === 'purge_rejected_applications' && ' This action permanently deletes records and cannot be undone.'}
          </Typography>
          <TextField
            label='Type CONFIRM to proceed'
            fullWidth size="small"
            value={confirmInput}
            onChange={e => setConfirmInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && confirmInput === 'CONFIRM') handleBulkExecute(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmDialog({ open: false }); setConfirmInput(''); }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleBulkExecute} disabled={confirmInput !== 'CONFIRM'}>
            Execute
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SuperUserAdmin;
